/**
 * Admin 系谱图 — binary placement drill-down (read-only, matches user PWA 安置树).
 */
import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { Button, Input, Space, Spin, Typography, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { api, ApiError } from '../api/client';
import { useQuery } from '@tanstack/react-query';

export type PlacementStepNode = {
  id: number;
  username: string;
  level_code: string;
  position?: string | null;
  has_left: boolean;
  has_right: boolean;
  left?: { id: number; username: string; level_code: string } | null;
  right?: { id: number; username: string; level_code: string } | null;
};

type Breadcrumb = { id: number; username: string };

const cardStyle: CSSProperties = {
  borderRadius: 12,
  padding: '16px 18px',
  background: 'linear-gradient(135deg, #1a2332 0%, #243044 100%)',
  border: '1px solid rgba(212, 175, 55, 0.25)',
  color: '#e8edf5',
  maxWidth: 520,
};

export function AdminGenealogyDrillViewer() {
  const [rootInput, setRootInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [stepLoading, setStepLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [breadcrumb, setBreadcrumb] = useState<Breadcrumb[]>([]);
  const [step, setStep] = useState<PlacementStepNode | null>(null);

  const loadStep = useCallback(async (userId: number) => {
    setStepLoading(true);
    try {
      const data = await api.get<PlacementStepNode>('/api/admin/binary-tree/placement-step', {
        user_id: userId,
      });
      setStep(data);
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '加载节点失败');
      setStep(null);
    } finally {
      setStepLoading(false);
    }
  }, []);

  const { data: networkRoot } = useQuery({
    queryKey: ['admin-binary-tree-network-root'],
    queryFn: () =>
      api.get<{ id: number; username: string }>('/api/admin/binary-tree/network-root'),
    staleTime: 300_000,
  });

  useEffect(() => {
    if (!networkRoot || currentUserId != null) return;
    setCurrentUserId(networkRoot.id);
    setBreadcrumb([{ id: networkRoot.id, username: networkRoot.username }]);
    void loadStep(networkRoot.id);
  }, [networkRoot, currentUserId, loadStep]);

  async function searchRoot() {
    const username = rootInput.trim();
    if (!username) {
      message.warning('请输入用户名');
      return;
    }
    setLoading(true);
    try {
      const user = await api.get<{ id: number; username: string }>('/api/admin/binary-tree/lookup', {
        username,
      });
      setCurrentUserId(user.id);
      setBreadcrumb([{ id: user.id, username: user.username }]);
      await loadStep(user.id);
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '用户未找到');
    } finally {
      setLoading(false);
    }
  }

  function drillTo(child: { id: number; username: string }) {
    const next = [...breadcrumb, { id: child.id, username: child.username }];
    setBreadcrumb(next);
    setCurrentUserId(child.id);
    void loadStep(child.id);
  }

  function goToCrumb(index: number) {
    const next = breadcrumb.slice(0, index + 1);
    const target = next[next.length - 1]!;
    setBreadcrumb(next);
    setCurrentUserId(target.id);
    void loadStep(target.id);
  }

  return (
    <div>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        打开即加载 PHP 网体根节点；也可输入用户名跳转。点击左/右区成员逐层往下浏览安置结构。
      </Typography.Paragraph>

      <Space.Compact style={{ width: '100%', maxWidth: 420, marginBottom: 12 }}>
        <Input
          placeholder="用户名（网体入口）"
          value={rootInput}
          onChange={(e) => setRootInput(e.target.value)}
          onPressEnter={() => void searchRoot()}
          disabled={loading}
        />
        <Button type="primary" icon={<SearchOutlined />} loading={loading} onClick={() => void searchRoot()}>
          进入系谱
        </Button>
      </Space.Compact>

      {breadcrumb.length > 0 && (
        <div style={{ marginBottom: 10, fontSize: 12, lineHeight: 1.8 }}>
          {breadcrumb.map((crumb, index) => (
            <span key={crumb.id}>
              {index > 0 && <span style={{ color: '#999', margin: '0 4px' }}>›</span>}
              <button
                type="button"
                onClick={() => goToCrumb(index)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  color: index === breadcrumb.length - 1 ? '#d4af37' : '#1677ff',
                  fontWeight: index === breadcrumb.length - 1 ? 600 : 400,
                }}
              >
                {crumb.username}
              </button>
            </span>
          ))}
        </div>
      )}

      {stepLoading && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      )}

      {!stepLoading && step && (
        <div style={cardStyle}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: '#8a96a8', marginBottom: 4 }}>当前节点</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#f0d78c' }}>{step.username}</div>
            <div style={{ fontSize: 11, color: '#8a96a8', fontFamily: 'monospace' }}>
              ID:{step.id} · {step.level_code}
              {step.position ? ` · ${step.position}` : ''}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
            <div
              style={{
                borderRadius: 8,
                border: '1px solid rgba(212,175,55,0.2)',
                padding: '10px 12px',
                minHeight: 56,
              }}
            >
              <div style={{ fontSize: 10, color: '#8a96a8', marginBottom: 4 }}>左区 (L)</div>
              {step.left ? (
                <button
                  type="button"
                  onClick={() => drillTo(step.left!)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    color: '#e8edf5',
                    fontSize: 13,
                    fontWeight: 600,
                    textAlign: 'left',
                  }}
                >
                  {step.left.username}
                  <span style={{ color: '#8a96a8', marginLeft: 6 }}>›</span>
                </button>
              ) : (
                <span style={{ fontSize: 12, color: '#6b7280' }}>空位</span>
              )}
            </div>
            <div
              style={{
                borderRadius: 8,
                border: '1px solid rgba(212,175,55,0.2)',
                padding: '10px 12px',
                minHeight: 56,
              }}
            >
              <div style={{ fontSize: 10, color: '#8a96a8', marginBottom: 4 }}>右区 (R)</div>
              {step.right ? (
                <button
                  type="button"
                  onClick={() => drillTo(step.right!)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    color: '#e8edf5',
                    fontSize: 13,
                    fontWeight: 600,
                    textAlign: 'left',
                  }}
                >
                  {step.right.username}
                  <span style={{ color: '#8a96a8', marginLeft: 6 }}>›</span>
                </button>
              ) : (
                <span style={{ fontSize: 12, color: '#6b7280' }}>空位</span>
              )}
            </div>
          </div>

          {(step.left || step.right) && currentUserId === step.id && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              点击左/右区成员名称进入下一层
            </Typography.Text>
          )}
        </div>
      )}

      {!stepLoading && !step && breadcrumb.length === 0 && !networkRoot && (
        <Typography.Text type="secondary">正在加载网体根节点…</Typography.Text>
      )}
    </div>
  );
}
